import { format } from "date-fns";

import { CustomAxios } from "@shared/api/axiosClient";

import type { Schemas } from "@shared/api/types";

export type LicenseApplication = Schemas["LicenseApplicationDTO"];
export type DaysAvailable = Schemas["DaysLicenseApplicationDTO"];
export type Supervisor = Schemas["SupervisorDTO"];

/** Valores del formulario. No coincide con el DTO: las fechas viven como Date hasta el submit. */
export interface RegisterRequestValues {
  supervisorEmail: string;
  type: string;
  startDate: Date;
  endDate: Date;
  comments?: string;
}
/** Lista de permisos solicitados por el colaborador. */
export const getLicenseApplications = (): Promise<LicenseApplication[]> =>
  CustomAxios.get("license-application/all").then(({ data }) => data);

/** Lista de dias disponibles del colaborador. */
export const getVacationDaysAvailable = (): Promise<DaysAvailable> =>
  CustomAxios.get("license-application/vacation-days-available").then(({ data }) => data);

/** Lista de tipos de solicitud disponibles */
export const getPickupFieldsMetadata = () =>
  CustomAxios.get("license-application/pickup-fields-metadata").then(
    ({ data }) => data.fields[0].pickListValues
  );

/** Lista de supervisores */
export const getSupervisors = (): Promise<Supervisor[]> =>
  CustomAxios.get("supervisors").then(({ data }) => data);

/** Crear solicitud de permiso del colaborador. */
export const createLicenseApplication = (formData: FormData): Promise<LicenseApplication> =>
  CustomAxios.post("license-application", formData).then(({ data }) => data);

/**
 * Construye el payload multipart para el POST de license-application.
 *
 * El campo file se agrega SOLO cuando `file` no es null. Enviar un Blob vacío
 * para tipos que no requieren archivo (ej. 'Otro') genera una parte multipart
 * ambigua que confunde el chequeo isEmpty() del backend, por eso se omite el
 * campo cuando no hay nada que adjuntar.
 *
 * @param {File|null} file  - El archivo seleccionado, o null si no se eligió ninguno.
 * @param {Object} value    - Valores del form (supervisorEmail, type, startDate, endDate, comments).
 * @returns {FormData}
 */
export function buildLicenseApplicationFormData(file: File | null, value: RegisterRequestValues) {
  const formData = new FormData();

  /**
   * El endpoint es multipart, asi que el DTO no viaja como JSON y no hay tipo generado que
   * lo cubra. Tipar la clave contra `LicenseApplication` es lo que sí se puede chequear:
   * si el backend renombra un campo del DTO, esto rompe en compilacion en vez de mandar
   * una parte multipart que el servidor ignora en silencio.
   */
  const append = (key: keyof LicenseApplication, fieldValue: string | Blob) =>
    formData.append(key, fieldValue);
  if (file !== null) {
    append("file", file);
  }
  append("supervisorEmail", value.supervisorEmail);
  append("type", value.type);
  /**
   * `toISOString()` convierte a UTC antes de recortar la fecha: como el picker arrastra
   * una hora de reloj, en zonas horarias al oeste de UTC eso puede correr el día
   * calendario elegido. `format` con "yyyy-MM-dd" toma el día calendario local, que es
   * lo que el usuario realmente seleccionó.
   */
  append("startDate", format(value.startDate, "yyyy-MM-dd"));
  append("endDate", format(value.endDate, "yyyy-MM-dd"));
  append("comments", value.comments ?? "");
  return formData;
}
